import React, { useEffect } from 'react';

import { track } from '@betterment/js-runtime';
import { Box, Button, H1, H2, HStack, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import AiOrb from './assets/icon_ai_orb.svg';
import { AccountHighlight } from './components/AccountHighlight';
import { ExplanationContent } from './components/ExplanationContent';
import { OpenAccountButton } from './components/OpenAccountButton';
import { RoundedBorderBox } from './components/RoundedBorderBox';
import { AccountRecommendationOptionQuery } from './graphql/query.accountRecommendationOption.generated';
import { useAccountRecommendation } from './hooks/useAccountRecommendation';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;

type Props = { initialRecommendationOption: AccountRecommendationOption };
export const RecommendationContent: React.FC<Props> = ({ initialRecommendationOption }) => {
  const { currentRecommendation, isExplanationLoading, isSkipLoading, generateExplanation, skipAccountRecommendation } =
    useAccountRecommendation({
      initialRecommendationOption,
    });
  const { option, explanation } = currentRecommendation;
  const reasonsToBelieve = useFallbackKeys(option.reasonsToBelieve);
  const isActionsEnabled = !isSkipLoading && !isExplanationLoading;

  useEffect(() => {
    const recommendation = option.name;
    track('ElementViewed', { name: 'AccountRecommendation', module: 'AccountRecommendation', recommendation });
  }, [option.name]);

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack css={{ gap: 'space.50' }}>
        <H1 variant="brandTitle" color="text" css={{ fontSize: 34 }}>
          Recommended for you
        </H1>
        <Text variant="text50" color="text.weak">
          Based on your financial profile and connected accounts.{' '}
          <SambaAutoLink
            to={urlBuilder.appFinancialProfileEdit.absolute()}
            onClick={() => trackClick({ name: 'ReviewProfile', module: 'AccountRecommendation' })}
          >
            Review profile
          </SambaAutoLink>
        </Text>
      </VStack>
      <HStack
        css={{
          background: 'background.neutral',
          borderRadius: 'large',
          overflow: 'hidden',
          alignItems: 'stretch',
          minHeight: 'fit-content',
        }}
      >
        <Box css={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <ThemedImage
            themedAsset={option.image}
            alt={option.name}
            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <VStack css={{ flex: 4, padding: 'space.300', gap: 'space.100' }}>
          <H2 variant="title200" color="text">
            {option.name}
          </H2>
          <VStack css={{ gap: 'space.0' }}>
            {reasonsToBelieve.map(({ item, key }) => (
              <AccountHighlight key={key} text={item} />
            ))}
          </VStack>
        </VStack>
      </HStack>
      {explanation || isExplanationLoading ? (
        <VStack css={{ gap: 'space.200', width: '100%' }}>
          <ExplanationContent
            explanation={explanation}
            postExplanationDisclosure={initialRecommendationOption.postExplanationDisclosure}
          />
          <Text variant="text50" color="text">
            Does your info look correct?{' '}
            <SambaAutoLink
              to={urlBuilder.appFinancialProfileEdit.absolute()}
              onClick={() => trackClick({ name: 'ReviewProfile', module: 'AccountRecommendation' })}
            >
              Review financial profile
            </SambaAutoLink>
          </Text>
        </VStack>
      ) : (
        <VStack css={{ gap: 'space.200' }}>
          <RoundedBorderBox borderStyle="gradientRotateForever" borderRadius={20} style={{ width: '100%' }}>
            <Button
              variant="textSecondary"
              css={{ borderRadius: '20px', textDecoration: 'none', background: 'none', width: '102%', padding: '1px' }}
              disabled={!generateExplanation}
              onClick={() => {
                if (generateExplanation) {
                  trackClick({
                    name: 'CreateExplanation',
                    module: 'AccountRecommendation',
                    recommendation: option.name,
                  });
                  void generateExplanation();
                }
              }}
            >
              <HStack
                css={{
                  px: 'space.150',
                  py: 'space.100',
                  borderRadius: '20px',
                  background: 'background.surface',
                  gap: 'space.50',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={AiOrb} alt="AI Icon" />
                <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                  Why we recommend this account
                </Text>
              </HStack>
            </Button>
          </RoundedBorderBox>
          <Text
            variant="text25"
            color="text.weak"
            dangerouslySetInnerHTML={{ __html: initialRecommendationOption.preExplanationDisclosure }}
          />
        </VStack>
      )}
      <HStack css={{ paddingTop: 'space.100', gap: 'space.100', alignItems: 'center' }}>
        <OpenAccountButton option={option} disabled={isSkipLoading} />
        {currentRecommendation.isSkipEligible && (
          <Button
            variant="ghostPrimary"
            loading={isSkipLoading}
            disabled={!isActionsEnabled}
            onClick={() => {
              if (skipAccountRecommendation) {
                trackClick({
                  name: 'NextRecommendation',
                  module: 'AccountRecommendation',
                  skippedRecommendation: option.name,
                });
                void skipAccountRecommendation();
              }
            }}
          >
            Next recommendation
          </Button>
        )}
      </HStack>
    </VStack>
  );
};
